import { apiClient } from "./apiClient";

const API_MEDIA_ROOT = "https://corporateserver.thedhms.com";

// Resolve relative image paths returned by the API to full URLs
const resolveImage = (path) => {
  if (!path) return null;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${API_MEDIA_ROOT}${path.startsWith("/") ? "" : "/"}${path}`;
};

// Normalize a raw family member from the API to the shape the UI expects.
// Spread raw first so explicit overrides always win over inconsistent API fields.
const normalizeMember = (raw, index = 0) => ({
  ...raw,
  id:             raw.id || raw._id || raw.memberid || `member-${index}`,
  name:           raw.fullname || raw.fullName || raw.name || raw.username || "Unknown",
  memberId:       raw.memberId || raw.member_id || raw.userdhmsid || raw.id || "N/A",
  email:          raw.email || "N/A",
  phone:          raw.phone || raw.memberphone || raw.phoneNumber || raw.phone_number || "",
  avatar:         resolveImage(raw.profileimage || raw.profileImage || raw.profile_image || raw.avatar || raw.image),
  role:           raw.role || raw.userRole || "Member",
  joinedDate:     raw.joinedDate || raw.joined_date || raw.date_joined || raw.savetimedata || "",
  assignedDevice: raw.assignedDevice || raw.assigned_device || raw.device || "None",
  deviceType:     raw.deviceType || raw.device_type || raw.type || "N/A",
  status:         raw.status || "healthy",
  devices:        raw.devices || raw.assignedDevices || raw.assigned_devices || [],
  numberOfAssignedDevices: raw.numberOfAssignedDevices ?? raw.number_of_assigned_devices ?? 0,
});

// Family Members related API calls for Family DHMS
export const membersService = {
  // POST /family/registerfamilymember - Register a new family member (multipart/form-data)
  async registerFamilyMember(memberData) {
    const fd = new FormData();
    fd.append("fullname", memberData.fullname);
    fd.append("phone",    memberData.phone);
    fd.append("email",    memberData.email);
    if (memberData.profileimage) fd.append("profileimage", memberData.profileimage);
    return apiClient.upload("/registerfamilymember", fd);
  },

  // GET /family/viewmembers - List all family members (normalized + client-side pagination)
  async list({ page = 1, limit = 10, search = "", status = "" } = {}) {
    const params = {};
    if (search) params.search = search;
    if (status && status !== "all") params.status = status;

    const response = await apiClient.get("/viewmembers", params);

    // Unwrap { status, data, message } envelope
    let rawList = null;
    if (Array.isArray(response))                           rawList = response;
    else if (Array.isArray(response?.data))                rawList = response.data;
    else if (Array.isArray(response?.members))             rawList = response.members;
    else if (Array.isArray(response?.results))             rawList = response.results;

    if (!rawList) {
      console.warn("[membersService] Unexpected /viewmembers response:", response);
      return { members: [], allMembers: [], totalPages: 1, currentPage: page, total: 0 };
    }

    const allMembers = rawList.map((m, i) => normalizeMember(m, i));
    const total = allMembers.length;

    // Client-side pagination (API returns all at once)
    const start = (page - 1) * limit;
    const members = allMembers.slice(start, start + limit);
    const totalPages = Math.max(1, Math.ceil(total / limit));

    return { members, allMembers, totalPages, currentPage: page, total };
  },

  // GET /family/viewmemberdetails/:id - Get family member details by ID
  async getById(id) {
    const response = await apiClient.get(`/viewmemberdetails/${encodeURIComponent(id)}`);
    // Unwrap { status, data, message } envelope
    const raw = (response?.data && typeof response.data === "object" && !Array.isArray(response.data))
      ? response.data
      : response;
    if (!raw) return null;

    // Normalize devices array if present
    const rawDevices = raw.devices || raw.assignedDevices || raw.assigned_devices || [];
    const devices = rawDevices.map((d) => ({
      ...d,
      id:              d.id || d._id || d.deviceId || d.device_id || "",
      name:            d.name || d.devicename || d.deviceName || d.device_name || "",
      model:           d.model || d.devicemodel || d.deviceModel || d.device_model || "",
      type:            d.type || d.devicetype || d.deviceType || d.device_type || "",
      serialNumber:    d.serialNumber || d.serialNo || d.serial_number || d.serial_no || "",
      operatingSystem: d.operatingSystem || d.deviceOS || d.operating_system || d.os || "",
      status:          d.status || d.devicestatus || d.deviceStatus || d.device_status || "",
      images:          d.images || d.deviceImages || d.device_images || [],
    }));

    return {
      ...normalizeMember({ ...raw, id: raw.id || raw._id || raw.memberid || id }),
      devices, // override with the device-normalized version above
    };
  },

  // PUT /family/editfamilymember/:id
  // Accepts: { memberfullname, memberphone, password } (all optional/nullable)
  async update(id, payload) {
    const body = {};
    const name = payload.memberfullname || payload.name || payload.fullname;
    const phone = payload.memberphone   || payload.phone;
    const pw    = payload.password;
    if (name)  body.memberfullname = name;
    if (phone) body.memberphone    = phone;
    if (pw)    body.password       = pw;
    return apiClient.put(`/editfamilymember/${encodeURIComponent(id)}`, body);
  },

  // DELETE /deletefamilymember/:id - Delete a family member
  async remove(id) {
    return apiClient.delete(`/deletefamilymember/${encodeURIComponent(id)}`);
  },

  // Legacy method for backward compatibility
  // POST /members (alias for registerFamilyMember)
  async create(payload) {
    return this.registerFamilyMember(payload);
  },
};

export default membersService;
